import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  ApiKeyUsageDetail,
  DailyUsageData,
  ModelUsageDetail,
} from '../../../utils/openai-api';
import { TICK_STYLE, CHART_COLORS, CHART_CONTENT_STYLE, TOOLTIP_WRAPPER_STYLE } from '../../UsageCharts/constants';

// --- Per-user daily cost chart ---

interface UserDailyCostChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

const formatDollar = (v: number) => `$${v.toFixed(2)}`;
export const USER_DAILY_COST_CHART_RANGES = new Set(['7days', '30days', '60days', '90days', '120days', '150days', '180days', '360days']);

const UserDailyCostTooltip: React.FC<UserDailyCostChartTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].filter(p => p.value > 0).sort((a, b) => b.value - a.value);
  const total = sorted.reduce((s, p) => s + p.value, 0);
  return (
    <div className="bg-gray-900 border border-gray-600 rounded-md p-2.5 shadow-2xl text-xs max-h-64 overflow-y-auto">
      <div className="text-gray-300 font-medium mb-1">{label}</div>
      <div className="text-white font-semibold mb-1">Total: {formatDollar(total)}</div>
      <div className="border-t border-gray-700 pt-1 space-y-0.5">
        {sorted.map(entry => (
          <div key={entry.name} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-400 truncate max-w-[180px]">{entry.name}</span>
            <span className="text-gray-200 ml-auto font-medium">{formatDollar(entry.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const UserDailyCostChart: React.FC<{
  apiKey: ApiKeyUsageDetail;
  dailyUsage: DailyUsageData[];
  globalModelUsage: ModelUsageDetail[];
}> = React.memo(({ apiKey, dailyUsage, globalModelUsage }) => {
  const { chartData, models, colorMap } = useMemo(() => {
    // Only show models that survive active filters (modelUsage is already filtered)
    const activeModelSet = new Set(Object.keys(apiKey.modelUsage ?? {}));
    const userModelCosts = apiKey.modelCosts ?? {};
    const hasDailyModelCosts = apiKey.dailyModelCosts && Object.keys(apiKey.dailyModelCosts).length > 0;

    // Models sorted by user's cost (top 8), excluding filtered-out models
    const sortedModels = Object.entries(userModelCosts)
      .filter(([model, cost]) => cost > 0 && activeModelSet.has(model))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([model]) => model);

    if (sortedModels.length === 0) return { chartData: [], models: [], colorMap: {} };

    let data: (Record<string, string | number> | null)[];

    if (hasDailyModelCosts) {
      // Direct lookup: iterate dailyModelCosts keys (always YYYY-MM-DD) to avoid
      // duplicating data when dailyUsage contains hourly rows for the same date.
      // Use dailyUsage date set to restrict to the visible date range.
      const visibleDates = new Set<string>();
      for (const d of dailyUsage) {
        visibleDates.add(d.date.slice(0, 10));
      }

      data = Object.entries(apiKey.dailyModelCosts!)
        .filter(([dateKey]) => visibleDates.has(dateKey))
        .map(([dateKey, dayCosts]) => {
          const point: Record<string, string | number> = { date: dateKey.slice(5, 10) };
          let hasAny = false;
          for (const model of sortedModels) {
            const cost = Math.round((dayCosts[model] ?? 0) * 100) / 100;
            point[model] = cost;
            if (cost > 0) hasAny = true;
          }
          return hasAny ? point : null;
        });
    } else {
      // Fallback: ratio-based distribution (for cached data without dailyModelCosts)
      const globalCostByModel: Record<string, number> = {};
      for (const m of globalModelUsage) {
        globalCostByModel[m.model] = (globalCostByModel[m.model] ?? 0) + m.cost;
      }
      const userFraction: Record<string, number> = {};
      for (const [model, userCost] of Object.entries(userModelCosts)) {
        if (!activeModelSet.has(model)) continue;
        const globalCost = globalCostByModel[model];
        userFraction[model] = globalCost > 0 ? userCost / globalCost : 0;
      }
      // Collapse hourly rows to one point per day before applying ratio
      const dayAgg = new Map<string, Record<string, number>>();
      for (const d of dailyUsage) {
        const dayKey = d.date.slice(0, 10);
        const existing = dayAgg.get(dayKey);
        const dayCosts = d.modelCosts ?? {};
        if (!existing) {
          dayAgg.set(dayKey, { ...dayCosts });
        } else {
          for (const [model, value] of Object.entries(dayCosts)) {
            existing[model] = (existing[model] ?? 0) + value;
          }
        }
      }
      data = Array.from(dayAgg.entries()).map(([dayKey, dayCosts]) => {
        const point: Record<string, string | number> = { date: dayKey.slice(5, 10) };
        let hasAny = false;
        for (const model of sortedModels) {
          const dailyModelCost = dayCosts[model] ?? 0;
          const userDailyCost = Math.round(dailyModelCost * (userFraction[model] ?? 0) * 100) / 100;
          point[model] = userDailyCost;
          if (userDailyCost > 0) hasAny = true;
        }
        return hasAny ? point : null;
      });
    }

    const filtered = data
      .filter((p): p is Record<string, string | number> => p !== null)
      .sort((a, b) => (a.date as string).localeCompare(b.date as string));

    const cMap: Record<string, string> = {};
    sortedModels.forEach((m, i) => { cMap[m] = CHART_COLORS[i % CHART_COLORS.length]; });

    return { chartData: filtered, models: sortedModels, colorMap: cMap };
  }, [apiKey, dailyUsage, globalModelUsage]);

  if (chartData.length === 0) return null;

  return (
    <div className="mt-2 w-full" style={{ maxWidth: 'calc(100vw - 1.5rem)' }}>
      <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Daily Cost</h5>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="date" tick={TICK_STYLE} stroke="#6b7280" />
          <YAxis tick={TICK_STYLE} stroke="#6b7280" tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}`} />
          <Tooltip content={<UserDailyCostTooltip />} wrapperStyle={TOOLTIP_WRAPPER_STYLE} contentStyle={CHART_CONTENT_STYLE} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {models.map(model => (
            <Bar key={model} dataKey={model} stackId="cost" fill={colorMap[model]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
UserDailyCostChart.displayName = 'UserDailyCostChart';
